'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppDownloadModal from '@/components/AppDownloadModal';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  subject: string;
  inquiryType: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const INQUIRY_TYPES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'partnership', label: 'Partnership Request' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'support', label: 'Support' },
  { value: 'press', label: 'Press / Media' },
];

const CONTACT_CARDS = [
  {
    icon: 'EnvelopeIcon',
    title: 'Email Us',
    description: 'For general inquiries and partnerships',
    value: 'hello@indianreviewclub.com',
    color: 'bg-orange-50 text-orange-600',
  },
  {
    icon: 'ClockIcon',
    title: 'Response Time',
    description: 'We typically respond within',
    value: '24–48 hours',
    color: 'bg-blue-50 text-blue-600',
  },
  {
    icon: 'MapPinIcon',
    title: 'Based In',
    description: 'Proudly serving brands & reviewers across',
    value: 'India 🇮🇳',
    color: 'bg-green-50 text-green-600',
  },
];

export default function ContactPageClient() {
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    inquiryType: 'general',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Message must be at least 20 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header onDownloadClick={() => setShowDownloadModal(true)} />
      <main className="min-h-screen bg-white pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden bg-white border-b border-gray-100">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-50 rounded-full blur-3xl opacity-60 translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-50 rounded-full blur-3xl opacity-50 -translate-x-1/3 translate-y-1/3" />
            <div
              className="absolute inset-0 opacity-[0.025]"
              style={{
                backgroundImage:
                  'radial-gradient(circle, #f97316 1px, transparent 1px)',
                backgroundSize: '28px 28px',
              }}
            />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 text-orange-600 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-5">
              <Icon name="ChatBubbleLeftRightIcon" size={14} />
              Get in Touch
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl text-gray-900 mb-4 leading-tight">
              We&apos;d love to{' '}
              <span className="text-primary">hear from you</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-500 max-w-xl mx-auto leading-relaxed">
              Whether you&apos;re a brand looking to grow, a reviewer with questions, or someone with a great idea — drop us a message.
            </p>
          </div>
        </section>

        {/* Contact Cards */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-6 relative z-10 mb-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {CONTACT_CARDS.map((card) => (
              <div
                key={card.title}
                className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${card.color}`}>
                  <Icon name={card.icon as any} size={20} />
                </div>
                <p className="font-semibold text-gray-900 text-sm mb-0.5">{card.title}</p>
                <p className="text-xs text-gray-400 mb-1">{card.description}</p>
                <p className="text-sm font-medium text-gray-700">{card.value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Form Section */}
        <section className="max-w-2xl mx-auto px-4 sm:px-6 pb-20">
          {submitted ? (
            <div className="bg-white border border-green-100 rounded-3xl p-10 text-center shadow-sm">
              <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Icon name="CheckCircleIcon" size={36} className="text-green-500" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">
                Message sent!
              </h2>
              <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                Thanks for reaching out, <strong>{formData.name}</strong>. We&apos;ve received your message and will get back to you within 24–48 hours.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({ name: '', email: '', subject: '', inquiryType: 'general', message: '' });
                }}
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary border border-primary/20 bg-orange-50 px-5 py-2.5 rounded-xl hover:bg-orange-100 transition-colors"
              >
                <Icon name="ArrowPathIcon" size={16} />
                Send another message
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-3xl p-6 sm:p-8 shadow-sm">
              <h2 className="font-heading font-bold text-xl text-gray-900 mb-1">Send us a message</h2>
              <p className="text-sm text-gray-400 mb-6">All fields marked with * are required.</p>

              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Full Name <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Rahul Sharma"
                      className={`w-full px-4 py-2.5 rounded-xl border text-sm text-gray-900 placeholder-gray-400 outline-none transition-colors focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                        errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                      }`}
                    />
                    {errors.name && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address <span className="text-primary">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="rahul@example.com"
                      className={`w-full px-4 py-2.5 rounded-xl border text-sm text-gray-900 placeholder-gray-400 outline-none transition-colors focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                        errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Inquiry Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Inquiry Type
                  </label>
                  <select
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 outline-none transition-colors focus:ring-2 focus:ring-primary/20 focus:border-primary hover:border-gray-300 appearance-none cursor-pointer"
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Subject <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm text-gray-900 placeholder-gray-400 outline-none transition-colors focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                      errors.subject ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={12} />
                      {errors.subject}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Message <span className="text-primary">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Tell us more about your inquiry..."
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm text-gray-900 placeholder-gray-400 outline-none transition-colors focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none ${
                      errors.message ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {errors.message ? (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />
                        {errors.message}
                      </p>
                    ) : (
                      <span />
                    )}
                    <span className={`text-xs ${formData.message.length < 20 && formData.message.length > 0 ? 'text-red-400' : 'text-gray-400'}`}>
                      {formData.message.length} / 20 min
                    </span>
                  </div>
                </div>

                {submitError && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <Icon name="ExclamationTriangleIcon" size={16} className="text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-600">{submitError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full cta-gradient text-white font-semibold text-sm py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-heading shadow-sm"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Icon name="PaperAirplaneIcon" size={16} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </section>
      </main>
      <Footer />
      <AppDownloadModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} />
    </>
  );
}
